import React, { useState, useCallback, useRef, useEffect } from 'react';
import { MatterDetailed } from '../../../../types/matters';
import { useMatterBilling } from '../../hooks/useMatterBilling';
import BillingOverviewSection from './billing-sections/BillingOverviewSection';
import TimeEntriesSection from './billing-sections/TimeEntriesSection';
import ExpensesSection from './billing-sections/ExpensesSection';
import TransactionsFeedSection from './billing-sections/TransactionsFeedSection';
import InvoicesSection from './billing-sections/InvoicesSection';
import TrustAccountSection from './billing-sections/TrustAccountSection';

interface BillingTabProps {
  matter: MatterDetailed;
}

type BillingSection = 'overview' | 'time' | 'expenses' | 'transactions' | 'invoices' | 'trust';

const BILLING_SECTIONS: { key: BillingSection; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'time', label: 'Time' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'trust', label: 'Trust' },
];

const BillingTab: React.FC<BillingTabProps> = ({ matter }) => {
  const [activeSection, setActiveSection] = useState<BillingSection>('overview');
  const [contentVisible, setContentVisible] = useState(true);
  const [renderKey, setRenderKey] = useState(0);

  // Sliding indicator state
  const tabBarRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  // Update sliding indicator position
  useEffect(() => {
    if (!tabBarRef.current) return;
    const activeBtn = tabBarRef.current.querySelector<HTMLButtonElement>('[data-active="true"]');
    if (activeBtn) {
      const barRect = tabBarRef.current.getBoundingClientRect();
      const btnRect = activeBtn.getBoundingClientRect();
      setIndicator({ left: btnRect.left - barRect.left, width: btnRect.width });
    }
  }, [activeSection]);

  const switchSection = useCallback((section: BillingSection) => {
    if (section === activeSection) return;
    setContentVisible(false);
    setTimeout(() => {
      setActiveSection(section);
      setRenderKey((k) => k + 1);
      requestAnimationFrame(() => setContentVisible(true));
    }, 150);
  }, [activeSection]);

  const {
    expenses, timeEntries, bills,
    trustLedger, trustTransactions,
    loading, stats,
    createTimeEntry, createExpense, createManualBill,
    createAndStartTimer, activeTimer, startTimer, pauseTimer, stopTimer,
    recordTrustDeposit,
  } = useMatterBilling(matter.id);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Billing</h3>
      </div>

      {/* Sub-tab navigation with sliding indicator */}
      <div className="relative flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg w-fit" ref={tabBarRef}>
        {BILLING_SECTIONS.map((section) => (
          <button
            key={section.key}
            data-active={activeSection === section.key}
            onClick={() => switchSection(section.key)}
            className={`relative z-10 px-3 py-1.5 text-sm rounded-md transition-colors duration-150 ${
              activeSection === section.key
                ? 'text-lex-accent font-medium'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            {section.label}
          </button>
        ))}
        {/* Sliding active indicator */}
        <div
          className="absolute top-1 bottom-1 rounded-md bg-white dark:bg-gray-700 shadow-sm transition-all duration-300"
          style={{ left: indicator.left, width: indicator.width, transitionTimingFunction: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }}
        />
      </div>

      {/* Loading indicator during crossfade */}
      {!contentVisible && (
        <div className="flex items-center justify-center py-4">
          <div className="h-4 w-4 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Active section with crossfade */}
      <div
        key={renderKey}
        style={{
          opacity: contentVisible ? 1 : 0,
          transition: 'opacity 150ms ease-out',
        }}
      >
        {activeSection === 'overview' && (
          <BillingOverviewSection matter={matter} stats={stats} />
        )}

        {activeSection === 'time' && (
          <TimeEntriesSection
            matter={matter}
            timeEntries={timeEntries}
            loading={loading}
            createTimeEntry={createTimeEntry}
            createAndStartTimer={createAndStartTimer}
            activeTimer={activeTimer}
            startTimer={startTimer}
            pauseTimer={pauseTimer}
            stopTimer={stopTimer}
          />
        )}

        {activeSection === 'expenses' && (
          <ExpensesSection
            matter={matter}
            expenses={expenses}
            loading={loading}
            createExpense={createExpense}
          />
        )}

        {activeSection === 'transactions' && (
          <TransactionsFeedSection
            expenses={expenses}
            timeEntries={timeEntries}
            trustTransactions={trustTransactions}
            loading={loading}
          />
        )}

        {activeSection === 'invoices' && (
          <InvoicesSection
            bills={bills}
            loading={loading}
            createManualBill={createManualBill}
          />
        )}

        {activeSection === 'trust' && (
          <TrustAccountSection
            matter={matter}
            trustLedger={trustLedger}
            trustTransactions={trustTransactions}
            stats={stats}
            recordTrustDeposit={recordTrustDeposit}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};

export default BillingTab;
